import { FlowResult, FollowUpAnswer, GemSalesConfig } from "./types";

export interface ClaimGuardrailResult {
  blockedClaims: string[];
  unsafeClaims: string[];
  requiresSafetyCaveat: boolean;
}

const medicalIntentTerms = ["medication", "medicine", "pregnant", "condition", "doctor", "healthcare professional"];

export function findBlockedClaims(text: string, config: GemSalesConfig): string[] {
  const normalized = text.toLowerCase();
  const blockedClaims = config.approvedCopy.blockedClaims ?? [];

  return blockedClaims.filter((claim) => buildClaimPattern(claim).test(normalized));
}

export function inspectFlowResult(result: FlowResult, config: GemSalesConfig): ClaimGuardrailResult {
  const text = [result.title, result.body, result.caution ?? ""].join(" ");
  const blockedClaims = findBlockedClaims(text, config);
  const unsafeClaims = blockedClaims.filter((claim) => !isNegatedClaim(text, claim));
  const normalized = text.toLowerCase();
  const requiresSafetyCaveat =
    blockedClaims.length > 0 || medicalIntentTerms.some((term) => normalized.includes(term.toLowerCase()));

  return {
    blockedClaims,
    unsafeClaims,
    requiresSafetyCaveat,
  };
}

export function getApprovedProofSnippet(topic: string | undefined, config: GemSalesConfig): string {
  if (!topic) return config.approvedCopy.fallbackProofResponse;

  return config.approvedCopy.approvedProofSnippets[topic] ?? config.approvedCopy.fallbackProofResponse;
}

export function answerFollowUpQuestion(question: string, config: GemSalesConfig): FollowUpAnswer {
  const topic = classifyFollowUpTopic(question);
  if (topic === "medication") {
    return {
      topic,
      answer: config.approvedCopy.medicationSafetyResponse,
      caution: config.approvedCopy.safetyCaveat,
    };
  }

  if (topic === "value") {
    return {
      topic,
      answer: config.approvedCopy.valueResponse,
    };
  }

  if (topic === "taste") {
    return {
      topic,
      answer: config.approvedCopy.tasteResponse,
    };
  }

  const answer: FollowUpAnswer = {
    topic,
    answer: getApprovedProofSnippet(topic === "unknown" ? undefined : topic, config),
  };
  if (topic === "unknown") answer.caution = config.approvedCopy.safetyCaveat;
  return answer;
}

export function sanitizeFlowResult(result: FlowResult, config: GemSalesConfig): FlowResult {
  const inspection = inspectFlowResult(result, config);
  if (!inspection.requiresSafetyCaveat) return result;

  return {
    ...result,
    body: inspection.unsafeClaims.length > 0 ? config.approvedCopy.fallbackProofResponse : result.body,
    caution: result.caution ?? config.approvedCopy.safetyCaveat,
  };
}

function classifyFollowUpTopic(question: string): string {
  const normalized = question.toLowerCase();
  if (includesAny(normalized, ["medication", "medicine", "pregnant", "condition", "doctor", "safe", "safety"])) return "medication";
  if (includesAny(normalized, ["heavy", "metal", "lead", "mercury", "arsenic"])) return "heavy_metals";
  if (includesAny(normalized, ["fulvic", "acid"])) return "fulvic";
  if (includesAny(normalized, ["coa", "certificate", "lab report", "lab"])) return "coa";
  if (includesAny(normalized, ["fake", "real", "authentic", "generic"])) return "fake";
  if (includesAny(normalized, ["prove", "proof", "claim", "testosterone", "guarantee", "result"])) return "limits";
  if (includesAny(normalized, ["price", "expensive", "value", "worth", "cost"])) return "value";
  if (includesAny(normalized, ["taste", "flavor", "gummies", "resin", "mix"])) return "taste";

  return "unknown";
}

function includesAny(value: string, keywords: string[]): boolean {
  return keywords.some((keyword) => value.includes(keyword));
}

function buildClaimPattern(claim: string): RegExp {
  const escaped = claim.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");

  return new RegExp(`\\b${escaped}\\b`, "i");
}

function isNegatedClaim(text: string, claim: string): boolean {
  const match = buildClaimPattern(claim).exec(text);
  if (!match) return false;

  const prefix = text.slice(Math.max(0, match.index - 32), match.index).toLowerCase();
  return /\b(does not|do not|not|never|without|no)\b/.test(prefix);
}
